import { useTranslation } from 'react-i18next';
import { getIn, useFormikContext } from 'formik';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { ConditionFormValue, RuleField } from '../types';
import { emptyConditionRow } from '../utils';
import { ConditionRow } from './ConditionRow';

export interface ConditionListEditorProps {
  readonly name: string;
  readonly fields: RuleField[];
  readonly fieldsByPath: Map<string, RuleField>;
  readonly disabled: boolean;
  readonly emptyMessage: string;
  // A DISABLED exact rule may legitimately hold zero conditions (`validate-rule-shape.ts:172`), so
  // the last row must be removable there. A severity override always needs one, so this stays off.
  readonly allowEmpty?: boolean;
}

export const ConditionListEditor = ({
  name,
  fields,
  fieldsByPath,
  disabled,
  emptyMessage,
  allowEmpty = false,
}: ConditionListEditorProps) => {
  const { t } = useTranslation('common');
  const { values, setFieldValue } = useFormikContext<unknown>();

  const rows = (getIn(values, name) as ConditionFormValue[] | undefined) ?? [];

  const replaceRows = (next: ConditionFormValue[]) => setFieldValue(name, next);

  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;

    if (target < 0 || target >= rows.length) {
      return;
    }

    const next = [...rows];

    [next[index], next[target]] = [next[target], next[index]];
    replaceRows(next);
  };

  return (
    <Box>
      {!rows.length && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {emptyMessage}
        </Typography>
      )}

      {rows.map((row, index) => (
        <ConditionRow
          key={row.uid}
          name={`${name}.${index}`}
          index={index}
          fields={fields}
          fieldsByPath={fieldsByPath}
          disabled={disabled}
          canRemove={allowEmpty || rows.length > 1}
          canMoveUp={index > 0}
          canMoveDown={index < rows.length - 1}
          onRemove={() => replaceRows(rows.filter((_row, rowIndex) => rowIndex !== index))}
          onMove={direction => handleMove(index, direction)}
        />
      ))}

      {!disabled && (
        <Button size="small" startIcon={<AddIcon />} onClick={() => replaceRows([...rows, emptyConditionRow()])}>
          {t('rules.editor.condition.addButton')}
        </Button>
      )}
    </Box>
  );
};
